import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { CodeDisplayName } from '@/types/patient-chart-api';

/**
 * Master data — CPT procedure code typeahead for the Procedures Add
 * flow (PROD-827 chart write surfaces).
 *
 * Wire (curl-verified by Andrew 2026-05-20):
 *
 *   GET /api/Master/ProcedureCodesList?searchQuery={query}
 *       → CodeDisplayName[]
 *
 * Each row uses **`displayName`** PascalCase (NOT `display` — the Flutter
 * class is `CodeDisplayName`). The CreateProcedure wire body ships
 * `code` with this exact shape verbatim.
 *
 * Stale-request safety: same 4-layer defense as `useAllergyCodeSearch`
 * and `useDiagnosisCodeSearch` — signal forwarded, enabled gating
 * below 2 chars, query-key cancellation, placeholderData semantics.
 */

const MIN_QUERY_LENGTH = 2;

export function useProcedureCodeSearch(query: string, enabled = true) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['master-procedure-codes', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<CodeDisplayName[]>(
        `/api/Master/ProcedureCodesList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: enabled && trimmed.length >= MIN_QUERY_LENGTH,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}
